import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import LanguageToggle from '@/components/LanguageToggle';
import Logo from '@/components/Logo';
import { useIsMobile } from '@/hooks/use-mobile';
import { useHaptics } from '@/hooks/use-haptics';
import { PullToRefresh } from '@/components/PullToRefresh';
import {
  Clock,
  Users,
  Trophy,
  Smartphone,
  MapPin,
  Star,
  ArrowRight,
  Shield,
  Car,
  User,
  Camera,
  Zap,
  Lock,
  RefreshCw
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MadisonChatBubble } from '@/components/MadisonChatBubble';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { triggerHaptic } = useHaptics();
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const features = [
    {
      icon: Clock,
      title: language === 'en' ? 'Real-Time Updates' : 'Actualizaciones en Tiempo Real',
      description: language === 'en' 
        ? 'Get accurate wait times from people crossing right now'
        : 'Obtén tiempos de espera precisos de personas cruzando ahora mismo'
    },
    {
      icon: Users,
      title: language === 'en' ? 'Community Powered' : 'Impulsado por la Comunidad',
      description: language === 'en'
        ? 'Crowdsourced data from real travelers like you'
        : 'Datos colaborativos de viajeros reales como tú'
    },
    {
      icon: Trophy,
      title: language === 'en' ? 'Earn Rewards' : 'Gana Recompensas',
      description: language === 'en'
        ? 'Get points for each report and redeem for prizes'
        : 'Obtén puntos por cada reporte y canjéalos por premios'
    },
    {
      icon: Zap,
      title: language === 'en' ? 'AI Predictions' : 'Predicciones de IA',
      description: language === 'en'
        ? 'Smart algorithms predict future wait times'
        : 'Algoritmos inteligentes predicen futuros tiempos de espera'
    }
  ];

  const borderCrossings = [
    {
      name: 'San Ysidro',
      status: language === 'en' ? 'Active' : 'Activo',
      waitTime: '35 min',
      reports: 12
    },
    {
      name: 'Otay',
      status: language === 'en' ? 'Active' : 'Activo',
      waitTime: '22 min',
      reports: 8
    },
    {
      name: 'Tecate',
      status: language === 'en' ? 'Active' : 'Activo',
      waitTime: '15 min',
      reports: 4
    }
  ];

  const handleGetStarted = async () => {
    if (isMobile) await triggerHaptic('light');
    setIsLoading(true);
    setTimeout(() => {
      navigate('/auth');
      setIsLoading(false);
    }, 200);
  };

  const handleViewLiveData = async () => {
    if (isMobile) await triggerHaptic('light');
    navigate('/');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    if (isMobile) await triggerHaptic('medium');
    
    // Simulate refresh action
    await new Promise(resolve => setTimeout(resolve, 1500));
    setRefreshing(false);
  };

  // Ensure page starts at top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PullToRefresh onRefresh={handleRefresh} enabled={!refreshing}>
      <div className="w-full min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 safe-area-inset overflow-x-hidden pb-20">
        {/* Header */}
        <header className="bg-background/80 backdrop-blur-sm border-b safe-area-inset w-full">
          <div className="w-full container mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <Logo priority={true} />
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-foreground">CruzaSmart</h1>
                  <p className="text-xs text-muted-foreground hidden sm:block">{t('tagline')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <LanguageToggle />
                <Button 
                  variant="outline" 
                  size={isMobile ? "sm" : "sm"}
                  onClick={handleGetStarted}
                  className="min-tap-target text-xs sm:text-sm px-3 sm:px-4"
                >
                  {t('signIn')}
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-6 sm:py-12 lg:py-16 px-4 sm:px-6">
          <div className="container mx-auto max-w-4xl text-center">
            <Badge variant="secondary" className="mb-4 sm:mb-6 text-xs sm:text-sm font-medium px-3 py-2 sm:px-4 sm:py-2 touch-manipulation">
              {language === 'en' ? 'Backed by 150,000+ Border Travelers' : 'Respaldado por más de 150,000 Viajeros Fronterizos'}
            </Badge>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-foreground mb-4 sm:mb-6 animate-fade-in leading-tight px-2">
              {language === 'en' 
                ? 'Know Before You Go. Stay in the Know. Move with Flow.' 
                : 'Porque esperar sin saber...desespera'
              }
            </h1>
            
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed px-4">
              {language === 'en'
                ? 'Get real-time border wait times, contribute to the community, and earn rewards for helping fellow travelers.'
                : 'Consulta tiempos de esperas reales, comparte tu cruce y gana recompensas por ayudar a miles a cruzar mejor!'
              }
            </p>

            <div className="flex flex-col gap-4 sm:flex-row sm:gap-4 justify-center mb-8 sm:mb-12 px-4 w-full">
              <Button 
                size={isMobile ? "lg" : "lg"}
                onClick={handleGetStarted}
                disabled={isLoading}
                className="w-full sm:w-auto text-base sm:text-lg px-8 py-6 bg-primary hover:bg-primary/90 animate-scale-in min-h-[56px] min-tap-target touch-manipulation active:scale-95 transition-transform shadow-lg"
              >
                {isLoading ? (
                  <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <Smartphone className="mr-2 h-5 w-5" />
                )}
                {language === 'en' ? 'Get Started Free' : 'Comenzar Gratis'}
                {!isLoading && <ArrowRight className="ml-2 h-5 w-5" />}
              </Button>
              
              <Button 
                variant="outline" 
                size={isMobile ? "lg" : "lg"}
                onClick={handleViewLiveData}
                className="w-full sm:w-auto text-base sm:text-lg px-8 py-6 hover-scale min-h-[56px] min-tap-target touch-manipulation active:scale-95 transition-transform"
              >
                <MapPin className="mr-2 h-5 w-5" />
                {language === 'en' ? 'View Live Data' : 'Ver Datos en Vivo'}
              </Button>
            </div>

            {/* Live Data Preview */}
            <div className="max-w-3xl mx-auto">
              <p className="text-center text-muted-foreground mb-4 sm:mb-6 text-sm sm:text-base px-2">
                {language === 'en' 
                  ? 'View exact wait times by signing in or registering for free. Earn points and rewards for sharing your crossing.'
                  : 'Consulta los tiempos de espera exactos al iniciar sesión o registrarte gratis. Gana puntos y recompensas por compartir tu cruce.'
                }
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {borderCrossings.map((crossing, index) => (
                  <Card 
                    key={crossing.name} 
                    className="animate-fade-in hover-scale touch-manipulation active:scale-95 transition-transform" 
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-foreground text-sm sm:text-base">{crossing.name}</h3>
                        <Badge variant="secondary" className="text-xs px-2 py-1">
                          {crossing.status}
                        </Badge>
                      </div>
                      
                      {user ? (
                        <div className="text-xl sm:text-2xl font-bold text-primary mb-1">
                          {crossing.waitTime}
                        </div>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={handleGetStarted}
                                className="w-full mb-1 text-xs py-3 rounded-lg border border-primary/40 hover:border-primary hover:bg-primary/10 bg-secondary/20 transition-all duration-200 min-tap-target touch-manipulation active:scale-95"
                              >
                                <Lock className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                                <span className="truncate">
                                  {language === 'en' 
                                    ? '🔒 Unlock wait time'
                                    : '🔒 Ver tiempo de espera'
                                  }
                                </span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-center">
                              <p className="text-sm">
                                {language === 'en'
                                  ? "Live wait times are available for registered users. It's free and you'll earn points for sharing!"
                                  : "Los tiempos de espera en vivo están disponibles solo para usuarios registrados. ¡Es gratis y ganas puntos por compartir!"
                                }
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      
                      <p className="text-xs text-muted-foreground">
                        {crossing.reports} {language === 'en' ? 'recent reports' : 'reportes recientes'}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
        </div>
      </section>

        {/* Features Section */}
        <section className="py-8 sm:py-12 lg:py-16 px-4 sm:px-6 bg-secondary/10">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3 sm:mb-4">
                {language === 'en' ? 'Why Choose CruzaSmart?' : '¿Por Qué Elegir CruzaSmart?'}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
                {language === 'en'
                  ? 'Join thousands of smart travelers who save time and help their community'
                  : 'Únete a miles de viajeros inteligentes que ahorran tiempo y ayudan a su comunidad'
                }
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {features.map((feature, index) => (
                <Card 
                  key={feature.title} 
                  className="animate-fade-in hover-scale touch-manipulation active:scale-95 transition-transform" 
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-4 sm:p-6 text-center">
                    <feature.icon className="h-8 sm:h-10 lg:h-12 w-8 sm:w-10 lg:w-12 text-primary mx-auto mb-3 sm:mb-4" />
                    <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-8 sm:py-12 lg:py-16 px-4 sm:px-6">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3 sm:mb-4">
                {language === 'en' ? 'How It Works' : 'Cómo Funciona'}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              <div className="text-center animate-fade-in">
                <div className="bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <MapPin className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-2">
                  {language === 'en' ? '1. Check In' : '1. Registrarse'}
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base px-2">
                  {language === 'en'
                    ? 'Tap "Begin Wait Time" when you arrive at the border'
                    : 'Toca "Llegué a la Línea" cuando llegues a la frontera'
                  }
                </p>
              </div>

              <div className="text-center animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <div className="bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Camera className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-2">
                  {language === 'en' ? '2. Share (Optional)' : '2. Compartir (Opcional)'}
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base px-2">
                  {language === 'en'
                    ? 'Take a photo of the line to help others'
                    : 'Toma una foto de la línea para ayudar a otros'
                  }
                </p>
              </div>

              <div className="text-center animate-fade-in sm:col-span-2 lg:col-span-1" style={{ animationDelay: '0.2s' }}>
                <div className="bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-2">
                  {language === 'en' ? '3. Earn Rewards' : '3. Gana Recompensas'}
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base px-2">
                  {language === 'en'
                    ? 'Get points for each check-in and redeem for prizes'
                    : 'Obtén puntos por cada registro y canjéalos por premios'
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 sm:py-12 lg:py-16 px-4 sm:px-6 bg-primary text-primary-foreground">
          <div className="container mx-auto max-w-3xl text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
              {language === 'en' 
                ? 'Ready to Save Time?' 
                : '¿Listo para Ahorrar Tiempo?'
              }
            </h2>
            <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 opacity-90 px-4">
              {language === 'en'
                ? 'Join the community of smart border crossers today.'
                : 'Únete hoy a la comunidad de cruzadores fronterizos inteligentes.'
              }
            </p>
            <Button 
              size={isMobile ? "default" : "lg"}
              variant="secondary"
              onClick={handleGetStarted}
              disabled={isLoading}
              className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 hover-scale min-tap-target touch-manipulation active:scale-95 transition-transform"
            >
              {isLoading ? (
                <RefreshCw className="mr-2 h-4 sm:h-5 w-4 sm:w-5 animate-spin" />
              ) : (
                <Smartphone className="mr-2 h-4 sm:h-5 w-4 sm:w-5" />
              )}
              {language === 'en' ? 'Get Started Free' : 'Comenzar Gratis'}
              {!isLoading && <ArrowRight className="ml-2 h-4 sm:h-5 w-4 sm:w-5" />}
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-6 sm:py-8 px-4 sm:px-6 border-t bg-background pb-safe">
          <div className="container mx-auto max-w-4xl">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <Logo />
                <div>
                  <p className="text-sm font-medium text-foreground">CruzaSmart</p>
                  <p className="text-xs text-muted-foreground hidden sm:block">{t('tagline')}</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-sm text-muted-foreground">
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={() => navigate('/privacy')}
                  className="p-0 h-auto text-muted-foreground hover:text-foreground min-tap-target touch-manipulation"
                >
                  {language === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
                </Button>
                <span className="text-xs">
                  © 2024 CruzaSmart
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>
      <MadisonChatBubble />
    </PullToRefresh>
  );
};

export default LandingPage;